(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-pino-editorial-root]';
  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();
  const transparentMediaTags = new Set(['A', 'BR', 'EM', 'IMG', 'SPAN', 'STRONG', 'U']);

  const createHeadingId = (root, heading, index, usedIds) => {
    const existingId = heading.id.trim();
    const validExistingId = existingId && !/[\s#]/u.test(existingId) && !usedIds.has(existingId);

    if (validExistingId) {
      usedIds.add(existingId);
      return existingId;
    }

    const sectionId = root.dataset.sectionId || 'article';
    const baseId = `PinoEditorialHeading-${sectionId}-${index + 1}`;
    let candidateId = baseId;
    let suffix = 2;

    while (usedIds.has(candidateId)) {
      candidateId = `${baseId}-${suffix}`;
      suffix += 1;
    }

    heading.id = candidateId;
    usedIds.add(candidateId);
    return candidateId;
  };

  const isImageOnlyParagraph = (paragraph) => {
    const images = Array.from(paragraph.querySelectorAll('img'));

    return paragraph.tagName === 'P'
      && images.length > 0
      && paragraph.textContent.trim() === ''
      && !paragraph.querySelector('video, iframe, svg')
      && Array.from(paragraph.querySelectorAll('*'))
        .every((element) => transparentMediaTags.has(element.tagName));
  };

  const nextSubstantiveSibling = (element) => {
    let sibling = element.nextElementSibling;

    while (sibling) {
      const isIgnorable = ['META', 'SCRIPT', 'STYLE'].includes(sibling.tagName)
        || sibling.classList.contains('pino-editorial-article__empty-paragraph')
        || (!sibling.textContent.trim() && !sibling.querySelector('img, video, iframe, svg'));

      if (!isIgnorable) {
        return sibling;
      }
      sibling = sibling.nextElementSibling;
    }

    return null;
  };

  const decorateImageParagraphs = (body) => {
    body.querySelectorAll('p').forEach((paragraph) => {
      if (!isImageOnlyParagraph(paragraph)) {
        return;
      }

      const images = Array.from(paragraph.querySelectorAll('img'));
      paragraph.classList.add('pino-editorial-article__media-block');
      paragraph.dataset.pinoImageCount = String(images.length);

      if (images.length >= 2) {
        paragraph.classList.add('pino-editorial-article__image-grid');

        if (images.length === 3) {
          paragraph.classList.add('pino-editorial-article__image-grid--3');
        } else if (images.length >= 4) {
          paragraph.classList.add('pino-editorial-article__image-grid--many');
        }

        if (paragraph.children.length === 1) {
          const wrapper = paragraph.firstElementChild;

          if (
            wrapper
            && ['A', 'EM', 'SPAN', 'STRONG', 'U'].includes(wrapper.tagName)
            && wrapper.querySelectorAll('img').length === images.length
          ) {
            wrapper.classList.add('pino-editorial-article__image-grid-inner');
          }
        }
      }
    });

    let singleImageRun = [];
    const flushSingleImageRun = () => {
      if (singleImageRun.length === 2) {
        singleImageRun[0].classList.add('pino-editorial-article__media-pair-first');
        singleImageRun[1].classList.add('pino-editorial-article__media-pair-second');
      }
      singleImageRun = [];
    };

    Array.from(body.children).forEach((child) => {
      if (
        child.classList.contains('pino-editorial-article__media-block')
        && child.dataset.pinoImageCount === '1'
      ) {
        singleImageRun.push(child);
      } else {
        flushSingleImageRun();
      }
    });
    flushSingleImageRun();
  };

  const decorateLead = (body) => {
    const children = Array.from(body.children);
    const firstHeading = children.find((child) => child.tagName === 'H2');
    const firstHeadingIndex = firstHeading ? children.indexOf(firstHeading) : children.length;
    const candidates = children
      .slice(0, firstHeadingIndex)
      .filter((child) => (
        child.tagName === 'P'
        && child.textContent.trim().length >= 4
        && child.textContent.trim().length <= 220
        && !child.classList.contains('pino-editorial-article__legacy-callout')
        && !child.classList.contains('pino-editorial-article__minor-heading')
        && !child.classList.contains('pino-editorial-article__media-block')
      ));

    if (candidates[0]) {
      candidates[0].classList.add('pino-editorial-article__lead');
    }
  };

  const decorateProductReferences = (body) => {
    Array.from(body.children).forEach((child) => {
      const text = child.textContent.trim();

      if (
        child.tagName !== 'P'
        || !text
        || text.length > 180
        || !/(?:今回使用した|今回ご紹介した|こちらのリンクからご購入)/u.test(text)
      ) {
        return;
      }

      const productLinks = Array.from(child.querySelectorAll('a[href*="/products/"]'));

      if (productLinks.length) {
        child.classList.add('pino-editorial-article__product-reference');
      }
    });
  };

  const decorateLegacyText = (body) => {
    const minorHeadingPattern = /^(?:[0-9０-９]{1,2}[.．]\s*|[①-⑳]\s*)/u;
    const benefitsHeadingPattern = /^◯.{1,32}(?:メリット|デメリット)$/u;
    const bracketHeadingPattern = /^【[^】]+】(?:【[^】]+】)+$/u;
    const listLinePattern = /^[・●■⇒]/u;
    const dividerPattern = /^[ー―—–━─\-=＝・･.]{6,}$/u;
    const hasCalloutPresentation = (element) => {
      const style = element.getAttribute('style') || '';

      return /(?:background\s*:|linear-gradient)/iu.test(style)
        || element.classList.contains('swl-marker');
    };
    const normaliseCalloutStyle = (element) => {
      ['background', 'color', 'font-family', 'font-size', 'line-height']
        .forEach((property) => element.style.removeProperty(property));
    };

    body.querySelectorAll('p').forEach((paragraph) => {
      const onlyElement = paragraph.children.length === 1 ? paragraph.firstElementChild : null;
      const hasOnlyWhitespaceText = Array.from(paragraph.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .every((node) => node.textContent.trim() === '');
      const text = paragraph.textContent.trim();
      const comparisonText = text.replace(/[\u00a0\u3000\s]+/gu, ' ');

      if (/^▼\s*目次$/u.test(text)) {
        paragraph.classList.add('pino-editorial-article__legacy-toc-label');
        return;
      }

      if (!text && !paragraph.querySelector('img, video, iframe, svg')) {
        paragraph.classList.add('pino-editorial-article__empty-paragraph');
        return;
      }

      if (/^\(A\)\s+\(B\)$/iu.test(comparisonText)) {
        paragraph.classList.add('pino-editorial-article__comparison-caption');
        return;
      }

      if (paragraph.parentElement === body && dividerPattern.test(text.replace(/\s+/gu, ''))) {
        paragraph.classList.add('pino-editorial-article__divider');
        return;
      }

      if (paragraph.parentElement === body && listLinePattern.test(text)) {
        paragraph.classList.add('pino-editorial-article__list-line');
      }

      if (
        paragraph.parentElement === body
        && text
        && onlyElement
        && hasOnlyWhitespaceText
        && onlyElement.tagName === 'STRONG'
        && !paragraph.querySelector('img, video, iframe, svg')
        && text.length <= 80
      ) {
        const isMinorHeading = minorHeadingPattern.test(text)
          || benefitsHeadingPattern.test(text)
          || bracketHeadingPattern.test(text)
          || text.includes('VS');

        if (isMinorHeading) {
          paragraph.classList.add('pino-editorial-article__minor-heading');
        } else if (/^正解は/u.test(text)) {
          paragraph.classList.add('pino-editorial-article__result-point');
        } else {
          const nextSibling = nextSubstantiveSibling(paragraph);

          if (
            nextSibling
            && nextSibling.classList.contains('pino-editorial-article__media-block')
            && !paragraph.classList.contains('pino-editorial-article__list-line')
          ) {
            paragraph.classList.add('pino-editorial-article__media-label');
          }
        }
      }

      if (
        paragraph.parentElement === body
        && onlyElement
        && hasOnlyWhitespaceText
        && onlyElement.tagName === 'SPAN'
        && text
        && (hasCalloutPresentation(paragraph) || hasCalloutPresentation(onlyElement))
      ) {
        paragraph.classList.add('pino-editorial-article__legacy-callout');
        normaliseCalloutStyle(onlyElement);
      }
    });

    body.querySelectorAll('h2, h3, h4').forEach((heading) => {
      if (!heading.textContent.trim() && !heading.querySelector('img, video, iframe, svg')) {
        heading.classList.add('pino-editorial-article__empty-heading');
      }
    });

    Array.from(body.children).forEach((child) => {
      if (child.tagName === 'SPAN' && child.textContent.trim()) {
        if (hasCalloutPresentation(child)) {
          child.classList.add('pino-editorial-article__legacy-callout');
          normaliseCalloutStyle(child);
        } else {
          child.classList.add('pino-editorial-article__direct-text');
        }
      }
    });

    let listRun = [];
    const flushListRun = () => {
      if (listRun.length) {
        listRun[0].classList.add('pino-editorial-article__list-line--first');
        listRun[listRun.length - 1].classList.add('pino-editorial-article__list-line--last');
      }
      listRun = [];
    };

    Array.from(body.children).forEach((child) => {
      if (child.classList.contains('pino-editorial-article__list-line')) {
        listRun.push(child);
      } else if (!child.classList.contains('pino-editorial-article__empty-paragraph')) {
        flushListRun();
      }
    });
    flushListRun();
  };

  const buildToc = (root, body, slot) => {
    if (root.dataset.showToc !== 'true' || !slot) {
      return;
    }

    const headings = Array.from(body.querySelectorAll('h2'))
      .filter((heading) => heading.textContent.trim() && !heading.closest('[hidden], [aria-hidden="true"]'));

    if (headings.length < 2) {
      return;
    }

    const usedIds = new Set(
      Array.from(document.querySelectorAll('[id]'))
        .filter((element) => !headings.includes(element))
        .map((element) => element.id)
        .filter(Boolean),
    );
    const nav = document.createElement('nav');
    const title = document.createElement('p');
    const list = document.createElement('ol');
    const navId = `PinoEditorialToc-${root.dataset.sectionId || 'article'}`;

    nav.className = 'pino-editorial-article__toc';
    nav.setAttribute('aria-labelledby', navId);
    title.className = 'pino-editorial-article__toc-title';
    title.id = navId;
    title.textContent = 'この記事の目次';
    list.className = 'pino-editorial-article__toc-list';

    headings.forEach((heading, index) => {
      const id = createHeadingId(root, heading, index, usedIds);
      const item = document.createElement('li');
      const link = document.createElement('a');

      link.href = `#${id}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', () => {
        window.requestAnimationFrame(() => {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        });
      });
      item.append(link);
      list.append(item);
    });

    nav.append(title, list);
    slot.replaceChildren(nav);
  };

  const initialiseProgress = (root) => {
    const bar = root.querySelector('[data-pino-editorial-progress]');

    if (!bar) {
      return () => {};
    }

    let animationFrame = 0;
    const update = () => {
      animationFrame = 0;
      const rootTop = root.getBoundingClientRect().top + window.scrollY;
      const start = rootTop - window.innerHeight * .16;
      const distance = Math.max(root.offsetHeight - window.innerHeight * .6, 1);
      const progress = Math.max(0, Math.min(1, (window.scrollY - start) / distance));
      bar.style.width = `${progress * 100}%`;
    };
    const requestUpdate = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    requestUpdate();

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  };

  const initialiseRoot = (root) => {
    if (!root || initializedRoots.has(root)) {
      return;
    }

    const body = root.querySelector('[data-pino-editorial-body]');
    const tocSlot = root.querySelector('[data-pino-editorial-toc-slot]');

    if (!body) {
      return;
    }

    initializedRoots.add(root);
    decorateImageParagraphs(body);
    decorateLegacyText(body);
    decorateLead(body);
    decorateProductReferences(body);
    buildToc(root, body, tocSlot);
    cleanupByRoot.set(root, initialiseProgress(root));
  };

  const rootsWithin = (container) => {
    const roots = [];

    if (container.matches && container.matches(ROOT_SELECTOR)) {
      roots.push(container);
    }
    container.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const initialiseWithin = (container = document) => {
    rootsWithin(container).forEach(initialiseRoot);
  };

  const onReady = () => initialiseWithin();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseWithin(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => {
      const cleanup = cleanupByRoot.get(root);

      if (cleanup) {
        cleanup();
        cleanupByRoot.delete(root);
      }
      initializedRoots.delete(root);
    });
  });
})();
